/**
 * @module DirectoryTree
 * @description A component to display a directory tree structure.
 *              ディレクトリツリー構造を表示するためのコンポーネント。
 */
import { type ScrollBarTapCircleOptions, type ScrollPaneInertiaOptions, VirtualScroll, type VirtualScrollHandle } from "@aiquants/virtualscroll"
import { ChevronDownIcon, ChevronRightIcon, DocumentTextIcon, FolderIcon } from "@heroicons/react/24/solid"
import { FileIcon } from "@phosphor-icons/react"
import type { CSSProperties, MouseEvent, ReactElement, ReactNode, Ref } from "react"
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { TreeLine } from "./TreeLine.tsx"
import type { DirectoryEntry, DirectoryEntryItemProps, DirectoryTreeProps, DirectoryTreeVirtualScrollOptions, TreeLineItemInfo } from "./types.ts"

/**
 * TailwindCSS v4 class names for DirectoryTree component
 * DirectoryTreeコンポーネント用の TailwindCSS v4 クラス名
 */
const directoryTreeClasses = {
    container: "directory-tree-container relative h-full transition-opacity duration-300 overflow-y-hidden pb-[5px] pr-[3px]",
    containerPending: "opacity-70",

    entry: "flex items-center cursor-pointer relative select-none",

    // ホバー時のスタイル（ライト/ダークモード対応）
    entryHover: twMerge("hover:bg-gray-400/15", "dark:hover:bg-gray-200/10"),

    // 選択時のスタイル（ライト/ダークモード対応）
    entrySelected: twMerge("bg-blue-400/10", "dark:bg-blue-400/15"),

    // アイテム選択時のスタイル（ライト/ダークモード対応）
    entryItemSelected: twMerge("bg-blue-400/20 shadow-[0_0_0_1px_rgba(59,130,246,0.3)]", "dark:bg-blue-400/25 dark:shadow-[0_0_0_1px_rgba(96,165,250,0.4)]"),

    // アイコンのスタイル
    expandIcon: twMerge("w-5 h-5 flex-shrink-0 flex items-center justify-center", "text-gray-500", "dark:text-gray-400"),

    expandIconSelected: twMerge("text-blue-700", "dark:text-blue-400"),

    typeIcon: twMerge("w-5 h-5 flex-shrink-0 flex items-center justify-center", "text-gray-500", "dark:text-gray-400"),

    typeIconSelected: twMerge("text-blue-700", "dark:text-blue-400"),

    // テキストのスタイル
    name: twMerge("overflow-hidden text-ellipsis whitespace-nowrap ml-1", "text-gray-700", "dark:text-gray-200"),

    nameDirectory: "font-bold",

    nameSelected: twMerge("text-blue-800 font-medium", "dark:text-blue-300"),
}

/**
 * カスタムスクロールバー非表示スタイル
 * Custom styles to hide scrollbar
 */
const scrollbarHideStyle: CSSProperties = {
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
}

/**
 * Webkit スクロールバー非表示のスタイルを動的に適用
 * Apply webkit scrollbar hiding styles dynamically
 */
const applyWebkitScrollbarHide = () => {
    // 既存のスタイルタグがあるかチェック
    let styleElement = document.getElementById("directory-tree-webkit-scrollbar-hide")

    if (!styleElement) {
        styleElement = document.createElement("style")
        styleElement.id = "directory-tree-webkit-scrollbar-hide"
        styleElement.textContent = ".directory-tree-container::-webkit-scrollbar { display: none; }"
        document.head.appendChild(styleElement)
    }
}

/**
 * Get combined class names for entry element
 * エントリー要素の結合されたクラス名を取得
 */
const getEntryClasses = (isHovered: boolean = false, isSelected: boolean = false, isItemSelected: boolean = false): string => {
    return twMerge(directoryTreeClasses.entry, isHovered && directoryTreeClasses.entryHover, isSelected && directoryTreeClasses.entrySelected, isItemSelected && directoryTreeClasses.entryItemSelected)
}

/**
 * Get combined class names for expand icon
 * 展開アイコンの結合されたクラス名を取得
 */
const getExpandIconClasses = (isSelected: boolean = false): string => {
    return twMerge(directoryTreeClasses.expandIcon, isSelected && directoryTreeClasses.expandIconSelected)
}

/**
 * Get combined class names for type icon
 * タイプアイコンの結合されたクラス名を取得
 */
const getTypeIconClasses = (isSelected: boolean = false): string => {
    return twMerge(directoryTreeClasses.typeIcon, isSelected && directoryTreeClasses.typeIconSelected)
}

/**
 * Get combined class names for name text
 * 名前テキストの結合されたクラス名を取得
 */
const getNameClasses = (isDirectory: boolean = false, isSelected: boolean = false): string => {
    return twMerge(directoryTreeClasses.name, isDirectory && directoryTreeClasses.nameDirectory, isSelected && directoryTreeClasses.nameSelected)
}

type DirectoryTreeVirtualScrollComponentProps<T> = {
    itemCount: number
    getItem: (index: number) => T
    getItemHeight: (index: number) => number
    viewportSize: number
    overscanCount?: number
    className?: string
    onScroll?: (scrollPosition: number, totalHeight: number) => void
    onRangeChange?: (renderingStartIndex: number, renderingEndIndex: number, visibleStartIndex: number, visibleEndIndex: number, scrollPosition: number, totalHeight: number) => void
    background?: ReactNode
    children: (item: T, index: number) => ReactNode
    initialScrollIndex?: number
    initialScrollOffset?: number
    tapScrollCircleOptions?: ScrollBarTapCircleOptions
    scrollBarWidth?: number
    enableThumbDrag?: boolean
    enableTrackClick?: boolean
    enableArrowButtons?: boolean
    enablePointerDrag?: boolean
    inertiaOptions?: ScrollPaneInertiaOptions
    ref?: Ref<VirtualScrollHandle>
}

const VirtualScrollExtended = VirtualScroll as unknown as <T>(props: DirectoryTreeVirtualScrollComponentProps<T>) => ReactElement

/**
 * @function getFileExtension
 * @description Extracts the extension from a filename.
 *              ファイル名から拡張子を抽出する。
 * @param {string} filename - The filename. ファイル名。
 * @returns {string} The file extension in lowercase. 小文字のファイル拡張子。
 */
const getFileExtension = (filename: string): string => {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase()
}

/**
 * @component DirectoryEntryItem
 * @description Renders a single entry (file or directory) in the directory tree. Memoized for performance.
 *              ディレクトリツリー内の単一エントリ (ファイルまたはディレクトリ) をレンダリングする。パフォーマンスのためにメモ化されている。
 * @param {DirectoryEntryItemProps} props - The props for the component.
 * @returns {JSX.Element} The DirectoryEntryItem component.
 */
const DirectoryEntryItem = memo<DirectoryEntryItemProps>(
    ({ entry, indentLevel, isDirOpen, onFileSelect, toggleDirectory, onToggleDirectoryRecursive, selectedPath, parentIsLast, renderChildren = true, selectionMode = "none", selectedItems, onSelectionChange }) => {
        const clickTimer = useRef<number | null>(null)
        const [childrenToRender, setChildrenToRender] = useState<DirectoryEntry[] | null>(null)

        useEffect(() => {
            // コンポーネントがアンマウントされる際にタイマーをクリアする
            return () => {
                if (clickTimer.current) {
                    window.clearTimeout(clickTimer.current)
                }
            }
        }, [])

        const isDirectory = entry.children !== undefined
        const isExpanded = isDirectory && isDirOpen(entry.absolutePath, entry.relativePath)
        const isSelected = entry.absolutePath === selectedPath
        const isItemSelected = !isDirectory && selectedItems?.has(entry.absolutePath)

        useEffect(() => {
            if (isExpanded && entry.children) {
                Promise.resolve().then(() => {
                    setChildrenToRender(entry.children ?? [])
                })
            } else {
                setChildrenToRender(null)
            }
        }, [isExpanded, entry.children])

        const handleInteraction = useCallback(
            (e: MouseEvent<HTMLDivElement>) => {
                e.stopPropagation()

                // 選択機能が有効で、かつファイルの場合のみ選択状態を変更
                if (selectionMode !== "none" && !isDirectory && onSelectionChange) {
                    onSelectionChange(entry.absolutePath, !isItemSelected)
                }

                if (isDirectory) {
                    // ディレクトリに対するクリック操作を処理する
                    if (clickTimer.current) {
                        // ダブルクリックを検出した場合、タイマーをクリアしてコールバックを実行する
                        window.clearTimeout(clickTimer.current)
                        clickTimer.current = null
                        onToggleDirectoryRecursive(entry)
                    } else {
                        // シングルクリックの場合、タイマーを設定してダブルクリックの可能性を待つ
                        clickTimer.current = window.setTimeout(() => {
                            toggleDirectory(entry.absolutePath, entry.relativePath)
                            clickTimer.current = null
                        }, 500) // 500ms をダブルクリックのしきい値とする
                    }
                } else {
                    // ファイルの場合は、ファイル選択イベントを発生させる
                    onFileSelect(entry.absolutePath, entry.relativePath)
                }
            },
            [isDirectory, entry, onToggleDirectoryRecursive, toggleDirectory, onFileSelect, selectionMode, onSelectionChange, isItemSelected],
        )

        // ホバー状態管理
        const [isHovered, setIsHovered] = useState(false)

        // Tailwindクラス名を動的に生成
        const entryClasses = getEntryClasses(isHovered, isSelected, isItemSelected)
        const expandIconClasses = getExpandIconClasses(isSelected || isItemSelected)
        const typeIconClasses = getTypeIconClasses(isSelected || isItemSelected)
        const nameClasses = getNameClasses(isDirectory, isSelected || isItemSelected)

        return (
            <Fragment key={entry.absolutePath}>
                <div
                    className={entryClasses}
                    style={{
                        paddingLeft: `${indentLevel * INDENT_SIZE}px`,
                        height: `${ITEM_HEIGHT}px`,
                    }}
                    onClick={handleInteraction}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleInteraction(e as unknown as MouseEvent<HTMLDivElement>)
                        }
                    }}
                    tabIndex={0}
                    role="treeitem"
                    aria-expanded={isDirectory ? isExpanded : undefined}
                    aria-label={`${entry.name} (${isDirectory ? "directory" : "file"})`}>
                    <span className={expandIconClasses}>{isDirectory ? isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" /> : <span className="w-4" />}</span>
                    <span className={typeIconClasses}>
                        {isDirectory ? (
                            <FolderIcon className="h-4 w-4" />
                        ) : (
                            (() => {
                                const extension = getFileExtension(entry.name)
                                switch (extension) {
                                    case "md":
                                        return <DocumentTextIcon className="h-4 w-4" />
                                    default:
                                        return <FileIcon className="h-4 w-4" />
                                }
                            })()
                        )}
                    </span>
                    <span className={nameClasses}>{entry.name}</span>
                </div>
                {isExpanded && childrenToRender && renderChildren && (
                    <fieldset>
                        {childrenToRender.map((child, index) => (
                            <DirectoryEntryItem
                                key={child.absolutePath}
                                entry={child}
                                indentLevel={indentLevel + 1}
                                isDirOpen={isDirOpen}
                                onFileSelect={onFileSelect}
                                toggleDirectory={toggleDirectory}
                                onToggleDirectoryRecursive={onToggleDirectoryRecursive}
                                selectedPath={selectedPath}
                                parentIsLast={[...parentIsLast, index === (childrenToRender || []).length - 1]}
                                selectionMode={selectionMode}
                                selectedItems={selectedItems}
                                onSelectionChange={onSelectionChange}
                            />
                        ))}
                    </fieldset>
                )}
            </Fragment>
        )
    },
    (prevProps, nextProps) => {
        // entry オブジェクトの主要なプロパティを比較する
        if (
            prevProps.entry.absolutePath !== nextProps.entry.absolutePath ||
            prevProps.entry.relativePath !== nextProps.entry.relativePath ||
            prevProps.entry.name !== nextProps.entry.name ||
            prevProps.entry.type !== nextProps.entry.type ||
            (prevProps.entry.children === undefined) !== (nextProps.entry.children === undefined)
        ) {
            return false // entry の基本情報が変更されていれば再レンダリングする
        }

        // ディレクトリの展開状態を比較する
        const prevIsDirectory = prevProps.entry.children !== undefined
        const nextIsDirectory = nextProps.entry.children !== undefined
        const prevIsExpanded = prevIsDirectory && prevProps.isDirOpen(prevProps.entry.absolutePath, prevProps.entry.relativePath)
        const nextIsExpanded = nextIsDirectory && nextProps.isDirOpen(nextProps.entry.absolutePath, nextProps.entry.relativePath)

        // その他の props を比較する
        if (
            prevProps.indentLevel !== nextProps.indentLevel ||
            prevIsExpanded !== nextIsExpanded ||
            prevProps.selectedPath !== nextProps.selectedPath ||
            prevProps.onFileSelect !== nextProps.onFileSelect || // onFileSelect はメモ化されていることを期待
            prevProps.toggleDirectory !== nextProps.toggleDirectory || // toggleDirectory はメモ化されていることを期待
            prevProps.onToggleDirectoryRecursive !== nextProps.onToggleDirectoryRecursive ||
            prevProps.selectionMode !== nextProps.selectionMode ||
            prevProps.selectedItems !== nextProps.selectedItems ||
            prevProps.onSelectionChange !== nextProps.onSelectionChange
        ) {
            return false // これらの props が変更されていれば再レンダリングする
        }

        // 親が最後の子であるかの情報を格納した配列を比較する
        if (prevProps.parentIsLast.length !== nextProps.parentIsLast.length) {
            return false
        }
        for (let i = 0; i < prevProps.parentIsLast.length; i++) {
            if (prevProps.parentIsLast[i] !== nextProps.parentIsLast[i]) {
                return false // 配列の要素が一つでも異なれば再レンダリングする
            }
        }

        return true // すべての比較条件で変更がなければ再レンダリングをスキップする
    },
)
DirectoryEntryItem.displayName = "DirectoryEntryItem"

// TreeLine コンポーネントが使用する props の型は TreeLine.tsx からインポートされる

const ITEM_HEIGHT = 20 // 各アイテムの高さ (px)
const INDENT_SIZE = 16 // 1階層あたりのインデント幅 (px)

/**
 * @function getAllDescendantPaths
 * @description Recursively gets all descendant directory paths for a given entry.
 *              指定されたエントリのすべての子孫ディレクトリパスを再帰的に取得する。
 * @param {DirectoryEntry} entry - The directory entry to start from. 開始点となるディレクトリのエントリ。
 * @returns {string[]} An array of absolute paths of all descendant directories. すべての子孫ディレクトリの絶対パスの配列。
 */
const getAllDescendantPaths = (entry: DirectoryEntry): string[] => {
    const paths: string[] = []
    if (entry.children) {
        for (const child of entry.children) {
            if (child.type === "directory") {
                paths.push(child.absolutePath)
                paths.push(...getAllDescendantPaths(child))
            }
        }
    }
    return paths
}

const getFlattenEntries = (entries: DirectoryEntry[], isExpanded: (absolutePath: string, relativePath: string) => boolean, parentIsLast: boolean[] = [], indentLevel = 0): { entry: DirectoryEntry; indentLevel: number; parentIsLast: boolean[] }[] => {
    return entries.flatMap((entry, index) => {
        const isLastChild = index === entries.length - 1
        const currentParentIsLast = [...parentIsLast, isLastChild]
        const currentEntry = { entry, indentLevel, parentIsLast: currentParentIsLast }
        if (entry.children && entry.children.length > 0 && isExpanded(entry.absolutePath, entry.relativePath)) {
            return [currentEntry, ...getFlattenEntries(entry.children, isExpanded, currentParentIsLast, indentLevel + 1)]
        }
        return [currentEntry]
    })
}

/**
 * @component DirectoryTree
 * @description A component to display a directory tree structure. It allows navigation and selection of files.
 *              ディレクトリツリー構造を表示するコンポーネント。ファイルのナビゲーションと選択が可能。
 * @param {DirectoryTreeProps} props - The props for the component.
 * @returns {JSX.Element} The DirectoryTree component.
 */
export const DirectoryTree = ({
    entries,
    onFileSelect,
    className,
    style,
    selectedPath,
    toggle,
    isExpanded,
    expandMultiple,
    collapseMultiple,
    isPending,
    doubleClickAction = "recursive",
    selectionMode = "none",
    selectedItems,
    onSelectionChange,
    lineColor = "#A0AEC0",
    virtualScrollOptions,
}: DirectoryTreeProps) => {
    const [isClient, setIsClient] = useState(false)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const virtualScrollRef = useRef<VirtualScrollHandle>(null)
    const [viewportHeight, setViewportHeight] = useState(0)
    const [renderingRange, setRenderingRange] = useState<{ renderStart: number; renderEnd: number } | null>(null)
    const {
        overscanCount: userOverscanCount,
        className: virtualScrollClassName,
        background: virtualScrollBackground,
        onScroll: virtualScrollOnScroll,
        onRangeChange: virtualScrollOnRangeChange,
        initialScrollIndex,
        initialScrollOffset,
        tapScrollCircleOptions,
        scrollBarWidth,
        enableThumbDrag,
        enableTrackClick,
        enableArrowButtons,
        enablePointerDrag,
        inertiaOptions,
    }: DirectoryTreeVirtualScrollOptions = virtualScrollOptions ?? {}
    const initialScrollPosition = typeof initialScrollOffset === "number" ? initialScrollOffset : 0
    const [scrollPosition, setScrollPosition] = useState(initialScrollPosition)
    const resolvedOverscanCount = typeof userOverscanCount === "number" ? userOverscanCount : 10

    const handleVirtualScroll = useCallback(
        (position: number, totalHeight: number) => {
            setScrollPosition(position)
            virtualScrollOnScroll?.(position, totalHeight)
        },
        [virtualScrollOnScroll],
    )

    const handleRangeChange = useCallback(
        (renderingStartIndex: number, renderingEndIndex: number, visibleStartIndex: number, visibleEndIndex: number, position: number, totalHeight: number) => {
            setRenderingRange((previous) => {
                if (previous && previous.renderStart === renderingStartIndex && previous.renderEnd === renderingEndIndex) {
                    return previous
                }
                return { renderStart: renderingStartIndex, renderEnd: renderingEndIndex }
            })
            virtualScrollOnRangeChange?.(renderingStartIndex, renderingEndIndex, visibleStartIndex, visibleEndIndex, position, totalHeight)
        },
        [virtualScrollOnRangeChange],
    )

    useEffect(() => {
        if (typeof initialScrollOffset === "number") {
            setScrollPosition(initialScrollOffset)
        }
    }, [initialScrollOffset])

    useEffect(() => {
        setIsClient(true)

        // Webkit スクロールバー非表示スタイルを適用
        applyWebkitScrollbarHide()

        if (containerRef.current) {
            setViewportHeight(containerRef.current.clientHeight)
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setViewportHeight(entry.contentRect.height)
                }
            })
            resizeObserver.observe(containerRef.current)
            return () => resizeObserver.disconnect()
        }
    }, [])

    const safeIsExpanded = useCallback(
        (path: string) => {
            return isClient ? isExpanded(path) : false
        },
        [isClient, isExpanded],
    )

    // Tailwindクラス名を動的に生成
    const containerClasses = isPending ? `${directoryTreeClasses.container} ${directoryTreeClasses.containerPending}` : directoryTreeClasses.container

    const handleDirectoryDoubleClick = useCallback(
        (entry: DirectoryEntry, overrideDoubleClickAction?: "toggle" | "recursive") => {
            const descendantPaths = getAllDescendantPaths(entry)
            const allPaths = [entry.absolutePath, ...descendantPaths]

            const action = overrideDoubleClickAction || doubleClickAction

            // ダブルクリックされたディレクトリと、そのすべての子孫ディレクトリが展開されているかを確認する
            const areAllExpanded = allPaths.every((path) => safeIsExpanded(path))

            if (areAllExpanded) {
                // すべてが開いている場合は、すべて折りたたむ
                collapseMultiple(allPaths)
            } else {
                // すべてが開いていない場合 (つまり、1つでも閉じている場合)

                // ダブルクリックされたディレクトリと、そのすべての子孫ディレクトリが閉じているかを確認する
                const areAllCollapsed = allPaths.every((path) => !safeIsExpanded(path))

                if (areAllCollapsed) {
                    // すべてが閉じている場合は、すべて展開する
                    expandMultiple(allPaths)
                } else if (action === "recursive") {
                    // 1つでも閉じている場合は、すべて展開する
                    expandMultiple(allPaths)
                } else if (action === "toggle") {
                    // 1つでも開いている場合は、すべて折りたたむ
                    collapseMultiple(allPaths)
                } else {
                    console.warn(`[DirectoryTree] Unknown double click action: ${action}. No action taken.`)
                }
            }
        },
        [safeIsExpanded, expandMultiple, collapseMultiple, doubleClickAction],
    )

    const allLineItems = useMemo(() => {
        if (entries.length === 0) {
            return [] // entries が空の場合は空配列を返す
        }
        const items: TreeLineItemInfo[] = []
        function collectItems(currentEntries: DirectoryEntry[], level: number, parentIsLastChain: boolean[]) {
            currentEntries.forEach((entry, index) => {
                const isDir = entry.children !== undefined
                const currentFullPath = entry.absolutePath
                const isCurrentlyExpanded = isDir && safeIsExpanded(currentFullPath)

                const lineItem: TreeLineItemInfo = {
                    id: currentFullPath,
                    name: entry.name,
                    absolutePath: currentFullPath,
                    indentLevel: level,
                    isLastChild: index === currentEntries.length - 1,
                    isDirectory: isDir,
                    isExpanded: isCurrentlyExpanded,
                    ancestorIsLast: [...parentIsLastChain], // 祖先の状態をコピーして渡す
                }
                items.push(lineItem)

                if (isDir && isCurrentlyExpanded && entry.children) {
                    collectItems(entry.children, level + 1, [...parentIsLastChain, index === currentEntries.length - 1])
                }
            })
        }
        collectItems(entries, 0, [])
        return items
    }, [entries, safeIsExpanded])

    const virtualItems = useMemo(() => {
        return getFlattenEntries(entries, safeIsExpanded)
    }, [entries, safeIsExpanded])

    // TreeLine に渡す幅を計算する
    const treeLineWidth = useMemo(() => {
        if (entries.length === 0) {
            return 0 // entries が空の場合は幅を 0 とする
        }
        let maxIndent = 0
        for (const item of allLineItems) {
            if (item.indentLevel > maxIndent) {
                maxIndent = item.indentLevel
            }
        }
        return (maxIndent + 2) * INDENT_SIZE // 最大インデントレベルに基づいて幅を計算し、余裕を持たせる
    }, [allLineItems, entries])

    const treeLineBackground = useMemo(() => {
        if (treeLineWidth <= 0 || viewportHeight <= 0) {
            return null
        }
        const renderStartIndex = renderingRange?.renderStart ?? Math.max(0, Math.floor(scrollPosition / ITEM_HEIGHT))
        const fallbackSpan = viewportHeight > 0 ? Math.ceil(viewportHeight / ITEM_HEIGHT) + resolvedOverscanCount : resolvedOverscanCount
        const fallbackEndIndex = virtualItems.length > 0 ? Math.min(virtualItems.length - 1, renderStartIndex + fallbackSpan) : renderStartIndex
        const renderEndIndex = renderingRange?.renderEnd ?? fallbackEndIndex
        const lookaheadEndIndex = virtualItems.length > 0 ? Math.min(virtualItems.length - 1, renderEndIndex + resolvedOverscanCount) : renderEndIndex
        return (
            <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
                <div className="absolute top-0 left-0" style={{ width: treeLineWidth, height: viewportHeight }}>
                    <TreeLine
                        items={allLineItems}
                        itemHeight={ITEM_HEIGHT}
                        width={treeLineWidth}
                        segmentWidth={INDENT_SIZE}
                        color={lineColor}
                        renderFromY={scrollPosition}
                        renderHeight={viewportHeight}
                        renderStartIndex={renderStartIndex}
                        renderEndIndex={renderEndIndex}
                        lookaheadEndIndex={lookaheadEndIndex}
                    />
                </div>
            </div>
        )
    }, [treeLineWidth, viewportHeight, renderingRange, scrollPosition, resolvedOverscanCount, virtualItems.length, allLineItems, lineColor])

    // エントリがない場合は、シンプルなコンテナのみをレンダリングする
    if (entries.length === 0) {
        return <div className={`${containerClasses} ${className || ""}`} style={{ ...style, ...scrollbarHideStyle }} />
    }

    return (
        <div ref={containerRef} className={`${containerClasses} ${className || ""}`} style={{ ...style, ...scrollbarHideStyle }} role="tree">
            <VirtualScrollExtended
                ref={virtualScrollRef}
                itemCount={virtualItems.length}
                getItem={(index) => virtualItems[index]}
                getItemHeight={() => ITEM_HEIGHT}
                viewportSize={viewportHeight}
                overscanCount={resolvedOverscanCount}
                onScroll={handleVirtualScroll}
                onRangeChange={handleRangeChange}
                className={virtualScrollClassName}
                background={
                    <>
                        {treeLineBackground}
                        {virtualScrollBackground}
                    </>
                }
                initialScrollIndex={initialScrollIndex}
                initialScrollOffset={initialScrollOffset}
                tapScrollCircleOptions={tapScrollCircleOptions}
                scrollBarWidth={scrollBarWidth}
                enableThumbDrag={enableThumbDrag}
                enableTrackClick={enableTrackClick}
                enableArrowButtons={enableArrowButtons}
                enablePointerDrag={enablePointerDrag}
                inertiaOptions={inertiaOptions}>
                {(item, _index) => {
                    if (!item) {
                        return null // item が undefined の場合は何もレンダリングしない
                    }
                    const { entry, indentLevel, parentIsLast } = item
                    return (
                        <DirectoryEntryItem
                            key={entry.absolutePath}
                            entry={entry}
                            indentLevel={indentLevel}
                            isDirOpen={safeIsExpanded}
                            onFileSelect={onFileSelect}
                            toggleDirectory={toggle}
                            onToggleDirectoryRecursive={handleDirectoryDoubleClick}
                            selectedPath={selectedPath}
                            parentIsLast={parentIsLast}
                            renderChildren={false}
                            selectionMode={selectionMode}
                            selectedItems={selectedItems}
                            onSelectionChange={onSelectionChange}
                        />
                    )
                }}
            </VirtualScrollExtended>
        </div>
    )
}

// スタイル関連のオブジェクトと関数を export
export { directoryTreeClasses, getEntryClasses, getExpandIconClasses, getNameClasses, getTypeIconClasses }
